import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable } from "rxjs";
import { filter, finalize } from "rxjs/operators";
import {
  AirgpuApiService,
  CreateAccountParams,
} from "src/app/core/http/airgpu-api.service";
import { AuthService } from "../auth.service";
import { Account } from "gen/typescript-angular-client";
import { environment } from "src/environments/environment";

// the account created in the first signup step is kept here so a user who
// navigates away before entering the code can come back to the verify step.
// Without it the account is only held in memory and the email address is
// already taken, leaving the signup impossible to finish.
const pendingSignupKey = "signup.account";

// a successful resend puts the button on a cooldown, matching the one minute
// window the api's send-activation-email path enforces. The deadline is
// persisted so reloading the page doesn't hand out a fresh allowance.
const resendCooldownKey = "signup.resendAvailableAt";
const resendCooldownSeconds = 60;

// what went wrong on the first signup step. Kept apart from a plain boolean
// because "this email is already taken" is not something retrying fixes, and
// telling the user to "try again in a bit" for it sends them in a circle.
export type SignupError = "" | "exists" | "captcha" | "password" | "generic";

// create-account labels its client errors with a machine-readable code. A Map
// rather than an object literal, so that a body carrying a code like
// "constructor" cannot resolve through Object.prototype into a truthy value
// that no message matches, leaving the form silent.
const errorCodes = new Map<string, SignupError>([
  ["user_already_exists", "exists"],
  ["invalid_captcha", "captcha"],
  ["invalid_password", "password"],
]);

// Newer api builds label the failure with a `code`; older ones only carry the
// message, so the status and the message are read as a fallback. That keeps the
// "already exists" message working whichever side deploys first.
export function signupErrorFor(err: HttpErrorResponse): SignupError {
  const code = err?.error?.code;
  if (typeof code === "string" && errorCodes.has(code)) {
    return errorCodes.get(code);
  }
  if (err?.status === 409) {
    return "exists";
  }
  const message = err?.error?.error;
  if (
    err?.status === 400 &&
    typeof message === "string" &&
    message.includes("already exists")
  ) {
    return "exists";
  }
  return "generic";
}

@Component({
  selector: "app-signup",
  templateUrl: "./signup.component.html",
  styleUrls: ["./signup.component.scss"],
})
export class SignupComponent implements OnInit, OnDestroy {
  public step: "signup" | "verify" | "redirect" = "signup";
  public authCheck$: Observable<boolean>;
  public signupForm: FormGroup;
  public verifyEmailForm: FormGroup;
  public error = false;
  // the signup step's own error, so it survives independently of `error`,
  // which the verify step owns
  public signupError: SignupError = "";
  public busy = false;
  public resent = false;
  // kept apart from `error` so a failed resend doesn't claim the code was wrong
  public resendError = false;
  public resendSecondsLeft = 0;
  private resendTimer?: number;
  public account: Account;
  private referralCode?: string;

  constructor(
    private fb: FormBuilder,
    private apiService: AirgpuApiService,
    private router: Router,
    private authService: AuthService,
    private route: ActivatedRoute
  ) {
    if (!environment.features.signup) {
      this.router.navigate(["/login"]);
    }

    const passwordPattern =
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@#$%^&+=!*])(?!.*\s).{8,}$/;

    this.signupForm = this.fb.group(
      {
        email: [undefined, [Validators.required, Validators.email]],
        password: [
          undefined,
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(passwordPattern),
          ],
        ],
        terms: [false, [Validators.requiredTrue]],
        marketing_consent: [false],
        captcha: [undefined, Validators.required],
        referral_code: [undefined],
      },
      { updateOn: "blur" }
    );

    this.verifyEmailForm = this.fb.group(
      {
        code: ["", [Validators.required]],
      },
      { updateOn: "blur" }
    );

    // kept on the component as well so it survives the form reset that
    // cancelling a signup performs
    this.route.queryParams.pipe(filter((p) => p.r)).subscribe((p) => {
      this.referralCode = p.r;
      this.signupForm.patchValue({
        referral_code: p.r,
      });
    });
  }

  ngOnInit(): void {
    this.authCheck$ = this.authService.isAuthenticated();
    this.authCheck$.subscribe((authenticated) => {
      if (authenticated) {
        this.router.navigate(["/machines"]);
      }
    });

    // resume a signup that was left before the email was verified
    const pendingAccount = this.readPendingSignup();
    if (pendingAccount) {
      this.account = pendingAccount;
      this.step = "verify";
      this.startResendCountdown();
    }
  }

  ngOnDestroy(): void {
    this.stopResendCountdown();
  }

  // reads the persisted deadline and ticks down to it
  private startResendCountdown() {
    this.updateResendSecondsLeft();
    if (this.resendSecondsLeft === 0 || this.resendTimer !== undefined) {
      return;
    }
    this.resendTimer = window.setInterval(() => {
      this.updateResendSecondsLeft();
      if (this.resendSecondsLeft === 0) {
        this.stopResendCountdown();
      }
    }, 1000);
  }

  private stopResendCountdown() {
    if (this.resendTimer !== undefined) {
      window.clearInterval(this.resendTimer);
      this.resendTimer = undefined;
    }
  }

  private updateResendSecondsLeft() {
    const availableAt = Number(localStorage.getItem(resendCooldownKey));
    if (!availableAt) {
      this.resendSecondsLeft = 0;
      return;
    }
    const remaining = Math.ceil((availableAt - Date.now()) / 1000);
    // a deadline in the future by more than the cooldown means a tampered or
    // stale value, so don't let it lock the button indefinitely
    this.resendSecondsLeft =
      remaining > resendCooldownSeconds
        ? resendCooldownSeconds
        : Math.max(remaining, 0);
  }

  private clearResendCooldown() {
    this.stopResendCountdown();
    localStorage.removeItem(resendCooldownKey);
    this.resendSecondsLeft = 0;
  }

  private readPendingSignup(): Account | undefined {
    const stored = localStorage.getItem(pendingSignupKey);
    if (!stored) {
      return undefined;
    }
    try {
      const account = JSON.parse(stored) as Account;
      // the cognito username is what verify and resend are keyed on, so a
      // record without it is unusable and only strands the user on the
      // verify step
      if (!account?.users?.length) {
        localStorage.removeItem(pendingSignupKey);
        return undefined;
      }
      return account;
    } catch {
      localStorage.removeItem(pendingSignupKey);
      return undefined;
    }
  }

  // workaround for safari not setting checkbox value automatically
  public toggleTerms(event: any) {
    this.signupForm.patchValue({
      terms: event.target.checked,
    });
  }

  // workaround for safari not setting checkbox value automatically
  public toggleMarketingConsent(event: any) {
    this.signupForm.patchValue({
      marketing_consent: event.target.checked,
    });
  }

  public signup() {
    this.signupError = "";

    if (!this.signupForm.valid) {
      this.signupForm.markAllAsTouched();
      return;
    }

    // ensure email is all lowercase
    this.signupForm.patchValue({
      email: (this.signupForm.value.email as string).toLowerCase(),
    });

    // create account
    this.busy = true;
    this.apiService
      .createAccount(this.signupForm.value as CreateAccountParams)
      .pipe(finalize(() => (this.busy = false)))
      .subscribe(
        (account) => {
          this.account = account;
          localStorage.setItem(pendingSignupKey, JSON.stringify(account));
          // a brand new account gets a fresh allowance
          this.clearResendCooldown();
          this.step = "verify";
        },
        (err: HttpErrorResponse) => {
          this.signupError = signupErrorFor(err);
          // the captcha token is single use, so clear it to force a new
          // challenge - otherwise every retry fails on the spent token
          this.signupForm.patchValue({ captcha: null });
        }
      );
  }

  public verifyEmail() {
    this.error = false;
    this.resendError = false;
    this.resent = false;

    if (!this.verifyEmailForm.valid) {
      this.verifyEmailForm.markAllAsTouched();
      return;
    }
    this.busy = true;
    this.authService
      .verifyEmail(this.account.users[0], this.code.value)
      .pipe(finalize(() => (this.busy = false)))
      .subscribe({
        complete: () => {
          this.step = "redirect";
          localStorage.removeItem(pendingSignupKey);
          this.clearResendCooldown();
          setTimeout(() => this.router.navigate(["/machines"]), 3000);
        },
        error: () => {
          this.error = true;
        },
      });
  }

  public resendConfirmationEmail() {
    this.error = false;
    this.resendError = false;
    this.resent = false;
    this.busy = true;
    this.authService
      .resendConfirmationEmail(this.account.users[0])
      .pipe(finalize(() => (this.busy = false)))
      .subscribe({
        complete: () => {
          this.resent = true;
          localStorage.setItem(
            resendCooldownKey,
            String(Date.now() + resendCooldownSeconds * 1000)
          );
          this.startResendCountdown();
        },
        error: () => {
          this.resendError = true;
        },
      });
  }

  // lets a user who signed up with the wrong email address get back to the
  // signup form instead of being stuck on the verify step forever
  public cancelSignup() {
    this.error = false;
    this.signupError = "";
    this.resendError = false;
    this.resent = false;
    this.busy = false;
    localStorage.removeItem(pendingSignupKey);
    this.clearResendCooldown();
    this.account = undefined;
    this.verifyEmailForm.reset({ code: "" });
    // the captcha control is nulled by the reset; the widget itself is not
    // mounted at this point, and the one *ngIf re-creates always renders
    // unsolved. ng-hcaptcha's writeValue does call hcaptcha.reset() before the
    // new widget has an id - verified to be a no-op rather than a throw.
    this.signupForm.reset({
      terms: false,
      marketing_consent: false,
      referral_code: this.referralCode,
    });
    this.step = "signup";
  }

  public get email() {
    return this.signupForm.get("email") as FormControl;
  }

  public get password() {
    return this.signupForm.get("password") as FormControl;
  }

  public get terms() {
    return this.signupForm.get("terms") as FormControl;
  }

  public get captcha() {
    return this.signupForm.get("captcha") as FormControl;
  }

  public get code() {
    return this.verifyEmailForm.get("code") as FormControl;
  }
}
